import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { ListRowProps } from 'react-virtualized';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, WindowScroller } from 'react-virtualized';

import type { AdRotation } from '../../../../common/SequenceWeight/types';
import type { Nullable } from '../../../../models';
import bem from '../../../../utils/bem';
import type { AdListOptionalFormState } from '../types';

const [block] = bem('virtualized-ad-list');

const DEFAULT_ROW_HEIGHT = 320;

type AdListElementWithIndex = {
  adListElement: AdListOptionalFormState;
  adIndex: number;
};

export type PreparedAdData = {
  adRotation: AdRotation;
  matchedElement: AdListElementWithIndex | undefined;
};

export interface VirtualizedAdListProps {
  readonly adsData: PreparedAdData[];
  readonly isSequence: boolean;
  readonly scrollElement?: Nullable<HTMLElement>;
  readonly renderAdItem: (
    adListElement: AdListOptionalFormState,
    adIndex: number,
    weight: number,
    isSequence: boolean,
    onContentChange?: () => void
  ) => JSX.Element;
}

function VirtualizedAdList({
  adsData,
  isSequence,
  scrollElement,
  renderAdItem,
}: VirtualizedAdListProps): Nullable<JSX.Element> {
  const cacheRef = useRef<CellMeasurerCache>(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: DEFAULT_ROW_HEIGHT,
    })
  );

  const listRef = useRef<List>(null);

  const adsDataKey = useMemo(() => adsData.map((d) => d.matchedElement?.adListElement.ad.id ?? '').join(','), [
    adsData,
  ]);

  // Clear cache and recompute row heights when ad data actually changes
  useLayoutEffect(() => {
    cacheRef.current?.clearAll();
    listRef.current?.recomputeRowHeights();
  }, [adsDataKey]);

  const cellMeasurerCache = cacheRef.current;

  const rowRenderer = useCallback(
    ({ key, index, style, parent }: ListRowProps): JSX.Element => {
      const item = adsData[index];
      const matched = item?.matchedElement;
      if (!matched) {
        return (
          <CellMeasurer cache={cellMeasurerCache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
            <div style={style} />
          </CellMeasurer>
        );
      }

      return (
        <CellMeasurer cache={cellMeasurerCache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          {({ measure, registerChild }) => (
            <div ref={registerChild as React.Ref<HTMLDivElement>} style={style}>
              {renderAdItem(matched.adListElement, matched.adIndex, item.adRotation.weight, isSequence, measure)}
            </div>
          )}
        </CellMeasurer>
      );
    },
    [adsData, isSequence, renderAdItem, cellMeasurerCache]
  );

  if (adsData.length === 0) return null;

  // WindowScroller allows parent component to handle scrolling of virtualized list (i.e. a side drawer)
  if (scrollElement) {
    return (
      <WindowScroller scrollElement={scrollElement}>
        {({ height, isScrolling, scrollTop, onChildScroll }): JSX.Element => (
          <AutoSizer disableHeight>
            {({ width }): JSX.Element => (
              <List
                ref={listRef}
                autoHeight
                width={width || 400}
                height={height || 600}
                rowCount={adsData.length}
                deferredMeasurementCache={cellMeasurerCache}
                rowHeight={cellMeasurerCache.rowHeight}
                rowRenderer={rowRenderer}
                isScrolling={isScrolling}
                scrollTop={scrollTop}
                onScroll={onChildScroll}
                overscanRowCount={10}
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    );
  }

  // Virtualized List will handle its own scrolling (i.e. On a dedicated page such as AdPage)
  return (
    <div className={block()}>
      <AutoSizer>
        {({ width, height }): JSX.Element => (
          <List
            ref={listRef}
            width={width || 400}
            height={height || 600}
            rowCount={adsData.length}
            deferredMeasurementCache={cellMeasurerCache}
            rowHeight={cellMeasurerCache.rowHeight}
            rowRenderer={rowRenderer}
            overscanRowCount={10}
          />
        )}
      </AutoSizer>
    </div>
  );
}

export default VirtualizedAdList;
